import {
    useState,
    useRef,
    createRef,
    useEffect,
} from 'react'

import {
    useQuery,
    useMutation,
} from '@tanstack/react-query'

import {
    useLocation,
    useNavigate,
} from 'react-router-dom'

import PublicHeader from '../../publicHeader'
import BackgroundCheckStepper from '../BackgroundCheckForm/Components/BackgroundCheckStepper'

import preCheckFormQuery from './services/pre-check-form'
import { useVerificationPolling } from './hooks/useVerificationPolling'
import savePreCheckConsentProgressMutation from './services/save-pre-check-consent-progress'
import consentUploadMutation from './services/consent-upload'
import submitPreCheckConsentMutation from './services/submit-pre-check-consent'
import submitPreCheckFormMutation from './services/submit-pre-check-form'

import Overlay from '../../Components/Overlay'
import FullScreenDialog from '../../Components/FullScreenDialog'

import { getElementWithInlineStylesAsync } from '../../helpers/component-helper'

import WelcomeAndStatusSelection from './Components/WelcomeAndStatusSelection'
import LegalDisclosuresAndConsentHub from './Components/LegalDisclosuresAndConsentHub'
import SignatureAndIdentifier from './Components/SignatureAndIdentifier'
import PreCheck from './Components/PreCheck'
import AdditionalDocument from './Components/AdditionalDocument'
import RetryDialog from './Components/RetryDialog'
import VerificationOverlay from './Components/VerificationOverlay'

const PreCheckForm = () => {

    const location = useLocation()

    const navigate = useNavigate()

    const refToPrint = useRef()

    const queryParams = new URLSearchParams(location.search)
    const key = queryParams.get('key')

    const {
        data,
        error,
        isLoading,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ['pre-check-form', key],
        queryFn: () => preCheckFormQuery(key),
        refetchOnWindowFocus: false,
    })

    const dataRef = useRef(data)
    dataRef.current = data

    const formUpdateIdRef = useRef(data?.formUpdateId)
    formUpdateIdRef.current = data?.formUpdateId

    const {
        error: savePreCheckConsentProgressError,
        isPending: savePreCheckConsentProgressIsPending,
        mutate: savePreCheckConsentProgress,
    } = useMutation({
        mutationFn: savePreCheckConsentProgressMutation,
        onSuccess: async (d) => {

            if (
                currentPage.type === "additional_document"
            ) {
                preCheckOnSubmitted()
                return
            }

            const nextPageIndex = currentPageIndex + 1

            if (currentPageIndex === welcomeAndStatusSelectionPageIndex) {
                const { data: freshData } = await refetch()
                if (freshData) {
                    dataRef.current = freshData
                }
            }

            if (currentPageIndex === signatureAndIdentifierPageIndex) {
                await submitPreCheckConsentAsync({
                    key,
                    currentPageType: currentPage.type,
                    currentPageDocumentId: currentPage.documentId,
                })
            }

            // continue next page
            setCurrentPageIndex(nextPageIndex)
            setCurrentStep(nextPageIndex)
        }
    })

    const {
        error: mutationSubmitError,
        isPending: mutationSubmitIsPending,
        mutate: submitPreCheckForm,
        data: mutationSubmitData,
    } = useMutation({
        mutationFn: submitPreCheckFormMutation,
        onSuccess: (d) => {

            if (d?.unsuccessfulMessage) {
                navigate('/pre-check-form-failed', {
                    state: { message: d.unsuccessfulMessage },
                })
                return
            }

            navigate('/pre-check-form-success')
        }
    })

    const {
        error: mutationConsentUploadError,
        isPending: mutationConsentUploadIsPending,
        mutate: consentUpload,
        data: mutationConsentUploadData,
    } = useMutation({
        mutationFn: consentUploadMutation,
        onSuccess: async (d) => {
            const payload = d.payloadToSaveApplicantProgress
            const pageType = d.pageType
            const url = d.url

            const page = payload?.data?.find((d => d.type === pageType))

            if (page?.data) {
                page.data.htmlUrl = url
            }

            savePreCheckConsentProgress(payload)
        },
        onError: () => {
            saveAdditionalPageRef.current = true
        }
    })

    const {
        isPending: submitPreCheckConsentIsPending,
        mutateAsync: submitPreCheckConsentAsync,
    } = useMutation({
        mutationFn: submitPreCheckConsentMutation,
    })

    const [currentPageIndex, setCurrentPageIndex] = useState(0)
    const [currentStep, setCurrentStep] = useState(0)
    const [showRetryDialog, setShowRetryDialog] = useState(false)

    const pageRefs = useRef([])

    const saveAdditionalPageRef = useRef(true)

    const {
        isFetching: formUpdateIdIsFetching,
        progressStatus,
        start: startPolling,
    } = useVerificationPolling({
        key,
        formUpdateIdRef,
        onSuccess: async () => {
            const { data: freshData } = await refetch()
            dataRef.current = freshData
            handleNext()
        },
        onFailure: () => setShowRetryDialog(true),
    })

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen my-4">
                <span className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
            </div>
        )
    }
    if (error) {
        return (
            <div>Error: {error.message}</div>
        )
    }

    if (!data) {
        return (
            <div>
                Data is not loaded yet.
            </div>
        )
    }

    if (data?.noFormMessage) {

        if (data?.status === "expired") {
            return (
                <div className="h-screen flex flex-col bg-gray-50">

                    <PublicHeader />

                    <div className="flex-grow flex items-center justify-center">
                        Expired
                    </div>

                </div>

            )
        }

        return (
            <div className="h-screen flex flex-col bg-gray-50">

                <PublicHeader />

                <p className="mt-1 text-base py-4 mx-auto">
                    {data.noFormMessage}
                </p>

            </div>
        )
    }

    const currentPage = data.Pages[currentPageIndex]

    let currentRef = pageRefs.current[currentPageIndex]

    if (!currentRef) {
        currentRef = createRef()
        pageRefs.current[currentPageIndex] = currentRef
    }

    const steps = data.Pages.map(p => p.title)

    const handlePrev = () => {
        const prevPageIndex = currentPageIndex - 1
        if (prevPageIndex < 0) {
            return
        }
        setCurrentPageIndex(prevPageIndex)

        setCurrentStep(prevPageIndex)

    }

    let nextButtonText = (
        currentPageIndex === data.Pages.length - 1 &&
        currentPage.type !== 'pre_check'
    ) ? "Submit" : "Next"

    const welcomeAndStatusSelectionPageIndex = data.Pages.findIndex(page => page.type === "welcome_and_status_selection")
    const signatureAndIdentifierPageIndex = data.Pages.findIndex(page => page.type === "signature_and_identifier")

    const handleNext = async () => {

        const nextPageIndex = currentPageIndex + 1
        const currentPageData = currentRef.current.validateAndGetData()
        if (!currentPageData.isValid) {
            return
        }

        const activeData = dataRef.current
        const activePage = activeData.Pages[currentPageIndex]
        activePage.data = currentPageData.value

        const dataToSubmit = activeData.Pages.map(page => {
            return {
                type: page.type,
                data: page.data,
                veriffIntegration: page.veriffIntegration,
                documentId: page.documentId,
                analyzeId: page.analyzeId,

            }
        })

        const payload = {
            key,
            data: dataToSubmit,
            currentPageType: currentPage.type,
            currentPageDocumentId: currentPage.documentId,
        }

        if (
            currentPage.type === "additional_document"
        ) {
            if (saveAdditionalPageRef.current) {
                saveAdditionalPageRef.current = false
                savePreCheckConsentProgress(payload)
                return
            }
            else {
                saveAdditionalPageRef.current = true
            }

        }

        if (refToPrint.current) {

            syncCheckedAttributes(refToPrint.current)

            const htmlContent = await getElementWithInlineStylesAsync(refToPrint.current)

            consentUpload({
                dataForUploadUrl: {
                    key,
                    consentType: activePage.type,
                },
                file: new File([htmlContent], `${activePage.type}.html`, { type: "text/html" }),
                payloadToSaveApplicantProgress: payload,
                pageType: activePage.type,
            })

            return
        }

        const activePreCheckPageIndex = activeData.Pages.findIndex(page => page.type === "pre_check")

        if (nextPageIndex === activePreCheckPageIndex) {
            savePreCheckConsentProgress(payload)
            return
        }

        if (nextPageIndex >= activeData.Pages.length) {

            submitPreCheckForm(payload)
            return
        }

        // continue next page
        setCurrentPageIndex(nextPageIndex)
        setCurrentStep(nextPageIndex)

    }

    const preCheckOnSubmitted = () => startPolling()

    // Called after a pre_check page is skipped (skip already recorded on the
    // backend by PreCheck). Deliberately runs only the "advance or submit"
    // branch of handleNext — no validation, consent upload, or progress-save
    // mutations.
    const preCheckOnSkipped = () => {
        const nextPageIndex = currentPageIndex + 1
        const activeData = dataRef.current

        if (nextPageIndex >= activeData.Pages.length) {
            const dataToSubmit = activeData.Pages.map(page => ({
                type: page.type,
                data: page.data,
                veriffIntegration: page.veriffIntegration,
                documentId: page.documentId,
                analyzeId: page.analyzeId,
            }))

            const payload = {
                key,
                data: dataToSubmit,
                currentPageType: currentPage.type,
                currentPageDocumentId: currentPage.documentId,
            }

            submitPreCheckForm(payload)
            return
        }

        setCurrentPageIndex(nextPageIndex)
        setCurrentStep(nextPageIndex)
    }


    return (
        <div className="h-screen flex flex-col bg-gray-50">

            <PublicHeader referenceNumber={data.referenceNumber} />

            <BackgroundCheckStepper steps={steps} currentStep={currentStep} />

            <div className="flex flex-col flex-grow items-center justify-center">
                {
                    currentPage.type === "welcome_and_status_selection" &&
                    <WelcomeAndStatusSelection
                        ref={currentRef}
                        data={currentPage.data}
                        handleNext={handleNext}
                        refToPrint={refToPrint}
                    />
                }
                {
                    currentPage.type === "legal_disclosures_and_consent_hub" &&
                    <LegalDisclosuresAndConsentHub
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                        company={currentPage.company}
                        handleNext={handleNext}
                    />
                }
                {
                    currentPage.type === "signature_and_identifier" &&
                    <SignatureAndIdentifier
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                        company={currentPage.company}
                        formKey={key}
                        handleNext={handleNext}
                        refetch={refetch}
                    />
                }
                {
                    currentPage.type === "pre_check" &&
                    <PreCheck
                        key={currentPage.documentId}
                        ref={currentRef}
                        data={currentPage.data}
                        company={currentPage.company}
                        onSubmitted={() => preCheckOnSubmitted()}
                        onSkipped={() => preCheckOnSkipped()}
                        handleNext={handleNext}
                        documentText={currentPage.title}
                        documentId={currentPage.documentId}
                        analyzeId={currentPage.analyzeId}
                        optional={currentPage.optional}
                        skipLabel={currentPage.skipLabel || 'Do not have US ID'}
                        formKey={key}
                        refetch={refetch}
                    />
                }

                {
                    (
                        currentPage.type === "additional_document"
                    ) &&
                    <AdditionalDocument
                        key={currentPage.documentId}
                        ref={currentRef}
                        data={currentPage.data}
                        requestKey={key}
                        title={currentPage.title}
                        description={currentPage.description}
                        documentId={currentPage.documentId}
                        documentOption={currentPage.documentOption}
                        analyzeId={currentPage.analyzeId}
                    />
                }

            </div>



            {(false) && (
                <p className="mt-1 text-base text-red-600 py-4 mx-auto">
                    Error
                </p>
            )}

            {
                (
                    currentPage.type !== "welcome_and_status_selection" &&
                    currentPage.type !== "signature_and_identifier" &&
                    currentPage.type !== "legal_disclosures_and_consent_hub"
                ) && (
                    <div className="flex justify-end gap-6 p-4 border-t">
                        <button
                            onClick={handlePrev}
                            disabled={currentPageIndex === 0}
                            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                        >
                            Prev
                        </button>
                        <button
                            onClick={handleNext}
                            className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed ${currentPage.type === 'pre_check' ? 'invisible pointer-events-none' : ''}`}
                        >
                            {nextButtonText}
                        </button>
                    </div>
                )
            }

            {/* Overlay */}
            {
                (
                    isFetching ||
                    savePreCheckConsentProgressIsPending ||
                    submitPreCheckConsentIsPending ||
                    mutationConsentUploadIsPending ||
                    mutationSubmitIsPending
                ) &&
                <Overlay />
            }

            {
                formUpdateIdIsFetching &&
                <VerificationOverlay
                    progressStatus={progressStatus}
                />
            }

            {
                showRetryDialog &&
                <RetryDialog
                    text="Results could not be retrieved yet. Please try again."
                    submitText="Retry"
                    onSubmit={() => {
                        setShowRetryDialog(false)
                        preCheckOnSubmitted()
                    }}
                />
            }

        </div>

    )
}

export default PreCheckForm

function syncCheckedAttributes(container) {
    const inputs = container.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    inputs.forEach((input) => {
        if (input.checked) {
            input.setAttribute("checked", "checked");
        } else {
            input.removeAttribute("checked");
        }
    });
}
